import { QuestionStep } from '../QuestionStep';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChoiceCard } from './_shared';
import {
  TYPOGRAPHY_FAMILY_OPTIONS,
  STEPS,
  type QuestionnaireAnswers,
} from '../questions';

interface TypographyStepProps {
  answers: QuestionnaireAnswers;
  onChange: (next: Partial<QuestionnaireAnswers>) => void;
}

const PREVIEW_STACKS: Record<string, { family: string; italic?: boolean }> = {
  'sans-serif': { family: 'Geist, ui-sans-serif, system-ui, sans-serif' },
  serif: { family: 'Georgia, "Iowan Old Style", serif' },
  mono: { family: '"Geist Mono", ui-monospace, SFMono-Regular, monospace' },
  display: { family: '"Instrument Serif", Georgia, serif', italic: true },
};

export function TypographyStep({ answers, onChange }: TypographyStepProps) {
  const step = STEPS.find((s) => s.id === 'typography')!;

  return (
    <QuestionStep step={step}>
      <div className="grid gap-3 sm:grid-cols-2">
        {TYPOGRAPHY_FAMILY_OPTIONS.map((opt) => {
          const stack = PREVIEW_STACKS[opt.value];
          return (
            <ChoiceCard
              key={opt.value}
              selected={answers.typography_family === opt.value}
              onSelect={() => onChange({ typography_family: opt.value })}
              label={opt.label}
              hint={opt.hint}
              preview={
                <div
                  className="w-full select-none rounded-sm border border-ink/10 bg-paper-2 px-3 py-2 text-2xl text-ink"
                  style={{
                    fontFamily: stack?.family,
                    fontStyle: stack?.italic ? 'italic' : 'normal',
                  }}
                >
                  Aa Bb Cc 0123
                </div>
              }
            />
          );
        })}
      </div>

      <div className="mt-8 grid max-w-xl gap-2">
        <Label htmlFor="preferred-fonts">
          Preferred fonts <span className="text-graphite text-xs">(optional)</span>
        </Label>
        <Input
          id="preferred-fonts"
          value={answers.typography_preferred_fonts ?? ''}
          onChange={(e) => onChange({ typography_preferred_fonts: e.target.value })}
          placeholder="e.g. Inter for body, Playfair Display for headings"
          maxLength={200}
        />
        <p className="font-mono text-[11px] text-graphite leading-relaxed">
          <span className="text-ochre">¶</span>&nbsp;&nbsp;Free-form. AURA tries to match Google Fonts; substitute on miss.
        </p>
      </div>
    </QuestionStep>
  );
}
